import axios from 'axios';
import { redirectToLogin } from '../utils/deferredNavigation';
import { getPracticeId } from '../config/practice';
import { captureAllowToken } from './allowToken';
import { unrestrictedRoutes } from '../permisiions';
import { maybeConfirmResend, withConfirmCapable } from './athenaConfirm';
const practiseid = getPracticeId();
const V1_BASE = process.env.REACT_APP_API_V1_BASE || 'https://api.prd.brainymed.com/ag';
const V2_BASE = process.env.REACT_APP_API_V2_BASE || 'https://api.prd.brainymed.com/v2/ag';

// ── v1 -> v2 gateway migration (brainymed/api docs/V1_TO_V2_MIGRATION.md) ──
// Routes on the allowlist served by v2 (/v1/client-routing/care) go to v2;
// everything else stays on v1. The list is fetched at startup and every 5 min,
// so moving or rolling back a route is a server-side file edit. Fail-safe: until
// the list loads, or if it cannot be loaded (v2 down), every call goes to v1.
let v2Rules = { enabled: false, version: '', list: [] };
const compileRule = (r) => {
  const src = String(r.path).split(/\{[^}]+\}/)
    .map((part) => part.replace(/[.*+?^$()|[\]\\]/g, '\\$&'))
    .join('[^/]+');
  const timeoutMs = Number.isInteger(r.timeout_ms) && r.timeout_ms >= 1000 ? r.timeout_ms : 60000;
  return { method: String(r.method).toUpperCase(), read: !!r.read, timeoutMs, path: r.path, re: new RegExp(`^${src}$`) };
};
let v2Epoch = 0; // bumped on every breaker trip: a rules fetch started before a trip must not re-enable routing
const loadV2Rules = () => {
  const epoch = v2Epoch;
  return axios.get(`${V2_BASE}/v1/client-routing/care`, { timeout: 4000 })
    .then(({ data }) => {
      if (epoch !== v2Epoch) return; // stale: the breaker tripped while this fetch was in flight
      const list = [];
      (data?.rules || []).forEach((r) => {
        try { list.push(compileRule(r)); } catch (e) { /* skip a bad rule, keep the rest */ }
      });
      v2Rules = { enabled: !!data?.enabled, version: data?.version || '', list };
    })
    .catch(() => { v2Rules = { enabled: false, version: '', list: [] }; });
};
loadV2Rules();
setInterval(loadV2Rules, 5 * 60 * 1000);

// Circuit breaker: a v2-routed call that got no usable answer (network error —
// which is how apache's CORS-less 502/503/504 pages look to the browser — or a
// timeout) switches routing off in this tab at once and re-checks in 30 s, so
// the next clicks go straight to v1 instead of failing until the 5-min refresh.
// Backoff: the first trip re-checks in 30 s, the second in 2 min; after that
// routing stays off until the regular 5-min refresh (a v2 whose routes hang while
// the rules endpoint still answers must not stall every tab every 90 s). Three
// successful v2-routed answers reset the backoff.
const V2_RECHECK_DELAYS = [30 * 1000, 2 * 60 * 1000];
let v2RecheckTimer = null;
let v2Trips = 0;
let v2Successes = 0;
const tripV2 = () => {
  v2Epoch += 1;
  v2Rules = { ...v2Rules, enabled: false };
  v2Successes = 0;
  const delay = V2_RECHECK_DELAYS[v2Trips];
  v2Trips += 1;
  if (delay && !v2RecheckTimer) {
    v2RecheckTimer = setTimeout(() => { v2RecheckTimer = null; loadV2Rules(); }, delay);
  }
};
const noteV2Success = () => {
  v2Successes += 1;
  if (v2Successes >= 3) v2Trips = 0;
};

// FastAPI's router-level answers when v2 has no handler for this request — the
// request never ran: 404 {"detail":"Not Found"} (no such path) or 405
// {"detail":"Method Not Allowed"} (path exists for other methods). Blob/arraybuffer
// bodies (downloads) are decoded first.
const ROUTER_MISS = { 404: 'Not Found', 405: 'Method Not Allowed' };
const isRouteMissing = async (resp) => {
  if (!resp || !ROUTER_MISS[resp.status]) return false;
  let d = resp.data;
  try {
    if (typeof Blob !== 'undefined' && d instanceof Blob) d = JSON.parse(await d.text());
    else if (typeof ArrayBuffer !== 'undefined' && d instanceof ArrayBuffer) d = JSON.parse(new TextDecoder().decode(d));
    else if (typeof d === 'string') d = JSON.parse(d);
  } catch (e) { return false; }
  return !!d && typeof d === 'object' && !Array.isArray(d)
    && Object.keys(d).length === 1 && d.detail === ROUTER_MISS[resp.status];
};

const matchV2Rule = (config) => {
  if (!v2Rules.enabled || !v2Rules.list.length) return null;
  const url = config.url || '';
  if (/^https?:/i.test(url) || url.includes('//')) return null; // absolute or malformed: leave alone
  const path = url.split('?')[0];
  const method = (config.method || 'get').toUpperCase();
  return v2Rules.list.find((r) => r.method === method && r.re.test(path)) || null;
};

const api = axios.create({
  baseURL: V1_BASE,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});
// ?allowtoken=: never stores the public intake token (see ./allowToken).
captureAllowToken('API');

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth'); 
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      localStorage.removeItem('auth');
    //  window.location.replace('/auth');
    }
    // Decide the gateway once per request; retries keep their decision.
    if (!config._gw) {
      const rule = matchV2Rule(config);
      if (rule) {
        config.baseURL = V2_BASE;
        config._gw = 'v2';
        config._gwRead = rule.read;
        // Tag switch-routed calls so the migration monitor can tell them apart
        // from clients that always used v2.
        config.params = { ...(config.params || {}), _gw: 2 };
        // A hung v2 must not hang a read: time out and fall back to v1. Never for
        // downloads (a large file legitimately takes long); rules may set timeout_ms.
        const isDownload = config.responseType === 'blob' || config.responseType === 'arraybuffer';
        if (rule.read && !config.timeout && !isDownload) { config.timeout = rule.timeoutMs; config._gwTimeoutSet = true; }
      } else {
        config._gw = 'v1';
      }
    }
    // document-filing routes: this client answers needs_confirm (./athenaConfirm.js)
    return withConfirmCapable(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);
let isRefreshing = false;

// True when no staff token is stored and the page is a sign-in-free route
// (permisiions.js unrestrictedRoutes).
const isAnonymousOnPublicPage = () => {
  try {
    if (window.localStorage.getItem('auth')) return false;
    const path = window.location.pathname || '';
    return unrestrictedRoutes.some((route) => path.startsWith(route));
  } catch (e) {
    return false;
  }
};

const refreshAuthToken = async () => {
  try {
    const accessToken = localStorage.getItem('auth');
    const response = await api.post(
      `/v1/${practiseid}/auth/refresh-token`,
      { "access_token": accessToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (response && response.status === 200) {
      localStorage.setItem('auth', response.refresh_token);
      return response.refresh_token;
    } else {
     //localStorage.removeItem('auth');
      redirectToLogin(); // queued while a recording runs (utils/deferredNavigation)
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    //localStorage.removeItem('auth');
    redirectToLogin(); // queued while a recording runs (utils/deferredNavigation)
    console.error("Failed to refresh token:", error);
    throw error;
  }
};

api.interceptors.response.use(
  (response) => {
    if (response?.config?._gw === 'v2') noteV2Success();
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // v2-routed call failed without a usable answer — retry once on v1 when that
    // cannot duplicate work. Browsers never see apache's own 502/503/504 (no CORS
    // headers): they surface as network errors (status 0), same as a timeout.
    //  - Route missing on v2 (FastAPI's bare 404): the request never ran -> retry
    //    reads AND writes.
    //  - No answer / 502 / 503 / 504: a write may already have run on v2 -> retry
    //    reads only; writes surface the error. Also trips the circuit breaker.
    // _gwfb in the query string makes every fallback visible in the access log.
    if (originalRequest && originalRequest._gw === 'v2' && !originalRequest._gwFallback
        && !axios.isCancel(error)) {  // a deliberately cancelled request stays cancelled
      const st = error.response ? error.response.status : 0;
      const noAnswer = !error.response || st === 502 || st === 503 || st === 504;
      if (noAnswer) tripV2();
      const routeMissing = await isRouteMissing(error.response);
      if (routeMissing || (noAnswer && originalRequest._gwRead)) {
        originalRequest._gwFallback = true;
        originalRequest._gw = 'v1';
        originalRequest.baseURL = V1_BASE;
        if (originalRequest._gwTimeoutSet) originalRequest.timeout = 0; // v1 keeps its old no-timeout behavior
        const { _gw: _dropTag, ...restParams } = originalRequest.params || {};
        originalRequest.params = { ...restParams, _gwfb: st || 'net' };
        return api(originalRequest);
      }
    }

    // A document the gateway will not file again without a person's OK (already in
    // the chart, or an earlier send got no answer): ask once, then replay with
    // ?confirm_resend=1 (./athenaConfirm.js, 2026-10-06).
    const resent = maybeConfirmResend(api, error);
    if (resent) return resent;

    if (error.response && error.response.status === 401 && !originalRequest._retry
        && isAnonymousOnPublicPage()) {
      // Nobody is signed in on a sign-in-free page (patient intake, the TV
      // board): there is no token to refresh, and a redirect to /login would
      // take the visitor off the page. The call just fails.
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await refreshAuthToken();
          if (newToken) {
            
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            isRefreshing = false;

            return api(originalRequest);
          }
        } catch (refreshError) {
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      } else {
    
       // localStorage.removeItem('auth');
     
        return Promise.reject(error);
      }
    }

    if (!originalRequest._retry) {
      //localStorage.removeItem('auth');
    }

    return Promise.reject(error);
  }
);

export default api;
